/**
 * Global API auth interceptor (T0-5 / T0.5-0).
 *
 * The backend now derives identity from server-signed tokens (X-Recruiter-Token,
 * X-Admin-Token) instead of trusting a client-supplied email — closing the IDOR
 * where anyone could read another recruiter's data by changing `recruiterEmail`.
 *
 * The recruiter portal has no central API client (raw fetch in components + the
 * default axios instance in *Api.js modules), so rather than edit ~23 files we
 * attach the token(s) in ONE place: a default-axios request interceptor plus a
 * guarded window.fetch wrapper. Both only touch requests aimed at our API and
 * never override a header a caller already set. Imported once from src/index.js.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE_URL } from '../config/api';

function getToken(name) {
  try {
    return sessionStorage.getItem(name) || localStorage.getItem(name) || '';
  } catch (e) {
    return '';
  }
}

function targetsApi(url) {
  if (!url) return false;
  if (url.startsWith('/api/')) return true; // same-origin relative
  return !!API_BASE_URL && url.indexOf(API_BASE_URL) !== -1;
}

// Auth endpoints where a 401 is expected (wrong password / not-yet-logged-in)
// and must NOT trigger an auto-logout redirect.
const AUTH_ENDPOINT_RE = /\/(admin-login|admin-login-v2|hirewithus-login|recruiter-login|employer-login|user-login|organizer\/login|check-session)/;

const AUTH_STORAGE_KEYS = [
  'admin_email', 'admin_id', 'admin_full_name', 'is_super_admin', 'admin_token', 'admin_role',
  'recruiter_id', 'recruiter_email', 'recruiter_company_name', 'recruiter_token',
  'employer_email', 'companyAccountId', 'company_name', 'company_token', 'employer_token',
  'company_user', 'user_email', 'user_token',
];

let _sessionExpiredHandled = false;

/**
 * Global "session expired" handler (enterprise auto-logout). When an authenticated
 * API call comes back 401, the token/session is gone — clear stored auth and bounce
 * to the login page with a flag so it can explain what happened. Guards against
 * loops and ignores the login/auth endpoints themselves.
 */
function handleUnauthorized(url) {
  try {
    if (_sessionExpiredHandled) return;
    if (!url || !targetsApi(url)) return;
    if (AUTH_ENDPOINT_RE.test(url)) return; // a 401 here is a failed login, not an expiry

    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
    if (path.includes('/login')) return; // already on a login page

    // Only act if the user actually looked logged in.
    const isAdmin = !!(getToken('admin_token') || getToken('admin_email'));
    const isEmployer = !!(getToken('employer_token') || getToken('employer_email'));
    const isRecruiter = !!(getToken('recruiter_token') || getToken('recruiter_email'));
    if (!isAdmin && !isEmployer && !isRecruiter) return;

    // Send recruiters to their own login; admins/employers to the shared login.
    const loginPath = (isRecruiter && !isAdmin && !isEmployer)
      ? '/recruiter/login'
      : '/hirewithus/login';

    _sessionExpiredHandled = true;
    AUTH_STORAGE_KEYS.forEach((k) => {
      try { localStorage.removeItem(k); } catch (e) { /* ignore */ }
      try { sessionStorage.removeItem(k); } catch (e) { /* ignore */ }
    });
    window.location.assign(`${loginPath}?session=expired`);
  } catch (e) {
    /* never throw from the interceptor */
  }
}

/**
 * A team member's role refused something they just tried. This never signs them
 * out. Reads stay quiet: a page handles its own empty state, and a load the person
 * didn't ask for shouldn't raise an alert. The fixed id keeps one toast on screen.
 */
function noticeRefusal(method, body) {
  try {
    const code = body && body.code;
    if (code !== 'no_permission' && code !== 'out_of_scope') return;
    if (String(method || 'GET').toUpperCase() === 'GET') return;
    toast.error(body.error || "Your role doesn't include this.", { id: 'company-access-refused' });
  } catch (e) {
    /* never throw from the interceptor */
  }
}

// 1) Default axios instance — covers the *Api.js modules (axios.get/post/...).
//    (Custom axios.create instances like creditsClient set their own token.)
axios.interceptors.request.use((config) => {
  try {
    const url = `${config.baseURL || ''}${config.url || ''}`;
    if (targetsApi(url) || targetsApi(config.url)) {
      const rt = getToken('recruiter_token');
      const at = getToken('admin_token');
      const et = getToken('employer_token');
      const ut = getToken('user_token');
      config.headers = config.headers || {};
      if (rt && !config.headers['X-Recruiter-Token']) config.headers['X-Recruiter-Token'] = rt;
      if (at && !config.headers['X-Admin-Token']) config.headers['X-Admin-Token'] = at;
      if (et && !config.headers['X-Employer-Token']) config.headers['X-Employer-Token'] = et;
      if (ut && !config.headers['X-User-Token']) config.headers['X-User-Token'] = ut;
    }
  } catch (e) {
    /* never break a request */
  }
  return config;
});

// 1b) Default axios response interceptor — auto-logout on 401 (expired session).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (error && error.response && error.response.status === 401) {
        const cfg = error.config || {};
        handleUnauthorized(`${cfg.baseURL || ''}${cfg.url || ''}` || cfg.url);
      }
      if (error && error.response && error.response.status === 403) {
        noticeRefusal(error.config && error.config.method, error.response.data);
      }
    } catch (e) {
      /* ignore */
    }
    return Promise.reject(error);
  }
);

// 2) window.fetch — covers component fetch() calls.
if (typeof window !== 'undefined' && window.fetch && !window.__apiAuthFetchPatched) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    let nextInit = init || {};
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (targetsApi(url)) {
        const rt = getToken('recruiter_token');
        const at = getToken('admin_token');
        const et = getToken('employer_token');
        const ut = getToken('user_token');
        if (rt || at || et || ut) {
          const headers = new Headers(
            (init && init.headers) ||
              (typeof input !== 'string' && input && input.headers) ||
              {}
          );
          if (rt && !headers.has('X-Recruiter-Token')) headers.set('X-Recruiter-Token', rt);
          if (at && !headers.has('X-Admin-Token')) headers.set('X-Admin-Token', at);
          if (et && !headers.has('X-Employer-Token')) headers.set('X-Employer-Token', et);
          if (ut && !headers.has('X-User-Token')) headers.set('X-User-Token', ut);
          nextInit = { ...nextInit, headers };
        }
      }
    } catch (e) {
      /* never break fetch */
    }
    const reqUrl = typeof input === 'string' ? input : (input && input.url) || '';
    return originalFetch(input, nextInit).then((response) => {
      try {
        if (response && response.status === 401) handleUnauthorized(reqUrl);
        if (response && response.status === 403 && targetsApi(reqUrl)) {
          const method = (init && init.method) || (typeof input !== 'string' && input && input.method);
          response.clone().json().then((body) => noticeRefusal(method, body)).catch(() => {});
        }
      } catch (e) {
        /* ignore */
      }
      return response;
    });
  };
  window.__apiAuthFetchPatched = true;
}

// 3) XMLHttpRequest — covers upload flows that use XHR for progress events
//    (e.g. resume uploads to /api/handle_resume_upload). The fetch/axios patches
//    above don't touch XHR, so without this the signed token isn't attached and
//    the request 401s cross-domain once the third-party session cookie is dropped.
if (typeof window !== 'undefined' && window.XMLHttpRequest && !window.__apiAuthXhrPatched) {
  const XHR = window.XMLHttpRequest;
  const origOpen = XHR.prototype.open;
  const origSend = XHR.prototype.send;
  const origSetRequestHeader = XHR.prototype.setRequestHeader;

  XHR.prototype.open = function (method, url, ...rest) {
    try {
      this.__apiAuthUrl = url;
      this.__apiAuthSetHeaders = {};
    } catch (e) { /* ignore */ }
    return origOpen.call(this, method, url, ...rest);
  };

  // Remember which headers the caller set so we never override them.
  XHR.prototype.setRequestHeader = function (name, value) {
    try {
      this.__apiAuthSetHeaders = this.__apiAuthSetHeaders || {};
      if (name) this.__apiAuthSetHeaders[String(name).toLowerCase()] = true;
    } catch (e) { /* ignore */ }
    return origSetRequestHeader.call(this, name, value);
  };

  XHR.prototype.send = function (body) {
    try {
      const url = this.__apiAuthUrl || '';
      if (targetsApi(url)) {
        const already = this.__apiAuthSetHeaders || {};
        const rt = getToken('recruiter_token');
        const at = getToken('admin_token');
        const et = getToken('employer_token');
        const ut = getToken('user_token');
        // Only add tokens the caller didn't already set. setRequestHeader is
        // valid here — the request is OPENED but not yet sent.
        if (rt && !already['x-recruiter-token']) origSetRequestHeader.call(this, 'X-Recruiter-Token', rt);
        if (at && !already['x-admin-token']) origSetRequestHeader.call(this, 'X-Admin-Token', at);
        if (et && !already['x-employer-token']) origSetRequestHeader.call(this, 'X-Employer-Token', et);
        if (ut && !already['x-user-token']) origSetRequestHeader.call(this, 'X-User-Token', ut);
      }
    } catch (e) {
      /* never break send */
    }
    return origSend.call(this, body);
  };

  window.__apiAuthXhrPatched = true;
}
